//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar, FormControl, FormControlLabel, makeStyles, Popover, Radio, RadioGroup, Grid } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent, unsubscribeRealtimeUpdates } from '../../system/firebase';
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { /*KEY_CODE_SEMICOLON,*/ UPDATED_INTERVIEW_REQUEST, REQUEST_INTERVIEW, EDIT_INTERVIEW_REQUEST, ANALYTICS_EVENT_INTERVIEW_CREATED } from '../../common/constants';
import { getNameByUser } from '../../common/textHelper';
import {
    PLACEHOLDER_FIELD_TO,
    // PLACEHOLDER_FIELD_NAMES,
    PLACEHOLDER_FIELD_JOB_TITLE,
    PLACEHOLDER_FIELD_STREET1,
    PLACEHOLDER_FIELD_STREET2,
    PLACEHOLDER_FIELD_ZIP_CODE,
} from '../../common/placeholderHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import useRequiredFields from '../../common/hooks/useRequiredFields';
import { updateInterview, saveNewInterview } from '../../common/services/empDashboardServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { getEmployerActiveJobsSuggestions } from '../../common/services/talentCenterServices';
import { validateEmail } from '../../common/emailHelper';
import { BASE_URL, DASHBOARD_ID_MY_SETTINGS, DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION } from '../../common/constants';
import { PATH_DASHBOARD } from '../../system/constants';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocompleteLite';
import AfitdDatePicker from '../../common/elemental/AfitdDatePicker';
import AfitdTimePicker from '../../common/elemental/AfitdTimePicker';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdStateAutocomplete from '../../common/elemental/AfitdStateAutocomplete';
import AfitdCityAutocomplete from '../../common/elemental/AfitdCityAutocomplete';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdButton from '../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import { addMinutes } from 'date-fns';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _find from 'lodash/find';
import _isNil from 'lodash/isNil';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        fieldContainerGroup: {
            display: 'flex',
            margin: '10px 0',
            alignItems: 'center',
        },
        fieldContainer: { flex: 1, padding: '0 5px' },
        fieldLabel: { padding: '5px 0', marginTop: 20, fontWeight: 'bold' },
        streetAddress1Label: { padding: '5px 0', fontWeight: 'bold' },
        active: { color: 'rgb(29,138,139)' },
        inactive: { color: 'rgba(17,30,20,0.4)' },
        locationContainer: {
            padding: 20,
            border: '1px solid #D5D7DD',
            borderRadius: 4,
        },
        hidden: { display: 'none' },
        infoIcon: { fontSize: 20, margin: '20px 0 0 10px', cursor: 'pointer' },
        inline: { display: 'flex', alignItems: 'center' },
        popover: { '& .MuiPaper-root': { width: 250, padding: 10 } },
        timeText: { margin: '0 16px' },
        date: {
            '& .MuiInputBase-input': { textTransform: 'uppercase', fontSize: 16 },
        },
        startTime: { '& .MuiInputBase-input': { fontSize: 16 } },
        endTime: { '& .MuiInputBase-input': { fontSize: 16 } },
        interviewerContainer: {
            marginBottom: '10px',
            marginTop: '10px',
        },
        removeButton: { width: '110px', backgroundColor: '#eb3939de', color: 'white', height: '55px' },

        interviewerActionButton: {
            margin: 'auto',
            alignItems: 'center',
            cursor: 'pointer',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        employerId,
        esToken,
        setEsToken,
        isDirty,
        isEditExistingData,
        jobSeekers,
        handleClickSaveButton,
        handleClickConfirmSendYesButton,
        data,
        handleChangeFields,
        // handleChangeAutoCompleteFields,
        handleChangeSend,
        handleChangeJobTitle,
        // handleChangePocNames, // deprecated
        handleChangeDateTime,
        handleChangeStartTime,
        handleClearAllButton,
        setPopover,
        popover,
        showClearAllModal,
        setShowClearAllModal,
        showSendButtonModal,
        setSendButtonModal,
        setData,
        myRef,
        cancelModal,
        setCancelModal,
        activeJobOptions,
        setActiveJobOptions,
        limitModalOpen,
        setLimitModalOpen,
        // handleKeyDownInterviewers, // deprecated
        // handleBlurInterviewers, // deprecated
        cleanUpAndClose,
        countryProps,
        stateProps,
        cityProps,
        stateLabelProps,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        onConfirmJsEngageLimitDialog,
    } = params;
    const {
        /*jobSeekerNames, */ jobSeekerId,
        jobSeekerName,
        jobSeekerProfilePicUrl,
        jobTitle,
        // pocNames, // deprecated
        date,
        startTime,
        endTime,
        virtual,
        streetAddress1,
        streetAddress2,
        postalCode,
    } = data;
    // console.log({ data });
    const { active: activeCls, inactive: inactiveCls, locationContainer: locationContainerCls, hidden: hiddenCls } = classes;
    const { open, jobSeekerId: jobSeekerIdProp, jobId: jobIdProp } = props;

    // expect only one item in the recipientList
    const recipientList = jobSeekerId ? [{ uid: jobSeekerId, jobSeekerName, profilePicUrl: jobSeekerProfilePicUrl }] : [];
    const recipientName = jobSeekerName;
    const recipientProfilePicUrl = jobSeekerProfilePicUrl; //recipient?.profilePicUrl;
    const recipientDisabled = jobSeekerIdProp !== undefined || isEditExistingData;

    const jobTitleDisabled = isEditExistingData || jobIdProp !== undefined;

    const saveButtonDisabled = !isDirty && (jobSeekerIdProp === undefined || jobIdProp === undefined);

    const confirmSendDialogTitle = isEditExistingData ? UPDATED_INTERVIEW_REQUEST : REQUEST_INTERVIEW;
    const confirmSendDialogMessage = isEditExistingData
        ? `This updated interview request will be sent to ${recipientName}. Do you wish to continue?`
        : `This Interview request will be sent to ${recipientName}. Do you wish to continue?`;

    // const jobTitlePlaceholder = jobTitle ? '' : PLACEHOLDER_FIELD_JOB_TITLE;

    // console.log({ recipient });
    const defns = {
        container: {
            open,
            onClose: () => (isDirty ? setCancelModal(true) : cleanUpAndClose()),
            label: isEditExistingData ? EDIT_INTERVIEW_REQUEST : REQUEST_INTERVIEW,
            actionButtonDefns: {
                clearAll: {
                    // disabled: isChange,
                    onClick: () => setShowClearAllModal(true),
                    label: 'Clear All',
                },
                cancel: {
                    disabled: false,
                    onClick: () => (isDirty ? setCancelModal(true) : cleanUpAndClose()),
                },
                save: {
                    disabled: saveButtonDisabled,
                    onClick: handleClickSaveButton,
                    label: 'Send',
                    startIcon: <SendIcon />,
                },
            },
        },
        send: {
            disabled: recipientDisabled,
            closeIcon: false,
            ChipProps: {
                onDelete: () => setData(prev => ({ ...prev, jobSeekerId: null, jobSeekerName: null, jobSeekerProfilePicUrl: null })),
                deleteIcon: <CloseIcon />,
                label: recipientName,
                avatar: <Avatar src={recipientProfilePicUrl} />,
            },
            multiple: true, // set to multiple so the Chip is displayed but it is actually a single-select
            options: recipientDisabled ? [] : jobSeekers,
            value: recipientList,
            getOptionLabel: option => getNameByUser(option),
            renderInput: params => <AfitdTextField {...params} placeholder={recipientList.length > 0 ? null : PLACEHOLDER_FIELD_TO} color="secondary" />,
            onChange: handleChangeSend,
            error: getFieldError('jobSeekerId'),
        },
        jobTitle: {
            disabled: jobTitleDisabled,
            options: activeJobOptions ? activeJobOptions : [],
            value: jobTitle,
            getOptionLabel: option => option?.jobTitle || option,
            placeholder: PLACEHOLDER_FIELD_JOB_TITLE,
            onChange: handleChangeJobTitle,
            error: getFieldError('jobTitle'),
            onSuggestionsFetchRequested: async value => {
                const keywords = value ? value : undefined;
                const options = await getEmployerActiveJobsSuggestions({ keywords, employerId, esToken, setEsToken });
                setActiveJobOptions(options);
            },
        },
        // pocNames: {
        //     multiple: true,
        //     freeSolo: true,
        //     options: [],
        //     value: pocNames,
        //     placeholder: pocNames.length > 0 ? '' : PLACEHOLDER_FIELD_NAMES,
        //     onChange: handleChangePocNames,
        //     onKeyDown: handleKeyDownInterviewers,
        //     onBlur: handleBlurInterviewers,
        // },
        date: {
            onChange: handleChangeDateTime.bind(null, 'date'),
            value: date,
            disabled: isEditExistingData,
            error: getFieldError('date'),
        },
        startTime: {
            onChange: handleChangeStartTime,
            minutesStep: 15,
            disabled: isEditExistingData,
            value: startTime,
            error: getFieldError('startTime'),
        },
        endTime: {
            onChange: handleChangeDateTime.bind(null, 'endTime'),
            value: endTime,
            minutesStep: 15,
            disabled: isEditExistingData,
            error: getFieldError('endTime'),
        },
        streetAddress1: {
            onChange: handleChangeFields,
            name: 'streetAddress1',
            value: streetAddress1,
            placeholder: PLACEHOLDER_FIELD_STREET1,
            error: getFieldError('streetAddress1'),
        },
        streetAddress2: {
            onChange: handleChangeFields,
            name: 'streetAddress2',
            value: streetAddress2,
            placeholder: PLACEHOLDER_FIELD_STREET2,
        },
        postalCode: {
            onChange: handleChangeFields,
            value: postalCode,
            name: 'postalCode',
            placeholder: PLACEHOLDER_FIELD_ZIP_CODE,
            error: getFieldError('postalCode'),
        },
        country: { ...countryProps },
        stateLabel: {
            ...stateLabelProps,
        },
        state: { ...stateProps },
        city: { ...cityProps },

        radioGroup: {
            row: true,
            value: virtual,
            name: 'virtual',
            onChange: handleChangeFields,
            onClick: () => {
                setTimeout(() => {
                    myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            },
        },
        virtualLabel: {
            value: 'virtual',
            control: <Radio disableRipple />,
            label: 'Virtual',
            labelPlacement: 'start',
            className: virtual === 'virtual' ? activeCls : inactiveCls,
        },
        physicalLabel: {
            value: 'physical',
            control: <Radio disableRipple />,
            label: 'Physical',
            labelPlacement: 'start',
            className: virtual === 'physical' ? activeCls : inactiveCls,
        },
        locationContainer: {
            className: cx(locationContainerCls, {
                [hiddenCls]: !(virtual === 'physical'),
            }),
        },
        infoIcon: {
            onClick: e => setPopover(e.target),
            'aria-describedby': 'pop-over',
        },
        popover: {
            id: 'pop-over',
            anchorEl: popover,
            open: popover,
            onClose: () => setPopover(null),
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
        },
        timeText: { variant: 'body2' },
        confirmClearAllDialog: {
            title: 'Clear All?',
            open: showClearAllModal,
            message: 'All unsaved changes will be lost.',
            onConfirm: handleClearAllButton,
            onClose: () => setShowClearAllModal(false),
        },
        confirmSendDialog: {
            title: confirmSendDialogTitle,
            open: showSendButtonModal,
            message: confirmSendDialogMessage,
            onConfirm: handleClickConfirmSendYesButton,
            onClose: () => setSendButtonModal(false),
        },
        confirmCancelDialog: {
            title: 'Cancel',
            open: cancelModal,
            onConfirm: () => {
                setCancelModal(false);
                cleanUpAndClose();
            },
            onClose: () => setCancelModal(false),
        },
        limitDialog: {
            open: limitModalOpen,
            onClose: () => setLimitModalOpen(false),
            onConfirm: onConfirmJsEngageLimitDialog,
            yesButtonLabel: 'Upgrade',
            yesButtonColor: 'secondary',
            message: 'You have reached your limit for candidate engagement for this Tier.  Please click below to Upgrade your subscription',
            title: 'Interaction Limit Exceeded',
        },
        pageErrorDialog: {
            message: 'You must enter all required fields.',
            open: pageErrorDialogOpen,
            onClose: () => setPageErrorDialogOpen(false),
        },
        interviewerContainer: { container: true, alignItems: 'flex-start', justifyContent: 'space-between', spacing: 1 },
        interviewDetails: { container: true, alignItems: 'baseline', justifyContent: 'space-between', spacing: 1 },
        interviewerDetailContainer: { item: true, xs: 11 },
        interviewerName: { item: true, xl: 6 },
        interviewerEmail: { item: true, xl: 6 },
        interviewerActionButton: { item: true, xs: 1 },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const getInitData = props => {
    const {
        interviewData,
        jobSeekerId: jobSeekerIdProp,
        jobSeekerName: jobSeekerNameProp,
        jobSeekerProfilePicUrl: jobSeekerProfilePicUrlProp,
        jobId: jobIdProp,
        jobTitle: jobTitleProp,
    } = props;

    let initData = {
        jobSeekerId: jobSeekerIdProp,
        jobSeekerName: jobSeekerNameProp,
        jobSeekerProfilePicUrl: jobSeekerProfilePicUrlProp,
        jobId: jobIdProp,
        jobTitle: jobTitleProp,
        date: new Date(),
        startTime: new Date().setMinutes('00'),
        endTime: new Date().setMinutes('30'),
        companyName: '',
        // pocNames: [], // deprecated
        virtual: 'virtual',
        streetAddress1: '',
        streetAddress2: '',
        postalCode: '',
        interviewers: [],
    };

    if (interviewData) {
        initData = {
            ...initData,
            date: interviewData.startDt,
            startTime: interviewData.startDt,
            endTime: interviewData.endDt,
            companyName: interviewData.companyName,
            // pocNames: interviewData.pocNames, // deprecated
            interviewers: interviewData?.interviewers ? interviewData?.interviewers : [],
            virtual: interviewData.virtual ? 'virtual' : 'physical',
            streetAddress1: interviewData.streetAddress1,
            streetAddress2: interviewData.streetAddress2,
            postalCode: interviewData.postalCode,
        };
    }

    return initData;
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const InterviewDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const history = useHistory();
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const { employerId, currentEmployer, lovs, sys, authUser, esToken, setEsToken, currentUser, userId } = appContext;
    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;
    const unsubscribeOnSnapshotListRef = useRef([]);
    const myRef = useRef(null);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { open, onClose, interviewData /* jobSeekerId: jobSeekerIdProp, jsEngageCount: jsEngageCountProp = 0 */ } = props;

    const interviewId = _get(interviewData, 'interviewId');
    const defaultCountry = _get(interviewData, 'country', null);
    const defaultState = _get(interviewData, 'state', null);
    const defaultCity = _get(interviewData, 'city', null);
    const defaultCityLocation = _get(interviewData, 'cityLocation', null);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [jobSeekers] = useState([]);
    const [popover, setPopover] = useState(false);
    const [showClearAllModal, setShowClearAllModal] = useState(false);
    const [showSendButtonModal, setSendButtonModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [activeJobOptions, setActiveJobOptions] = useState();
    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const [pageErrorDialogOpen, setPageErrorDialogOpen] = useState(false);
    const { invalidRequiredFields, setInvalidRequiredFields, getFieldError, clearInvalidRequiredField, validateRequiredFields } = useRequiredFields();
    const [isOkToSave, setIsOkToSave] = useState(false);
    const [interviewers, setInterviewers] = useState([
        {
            id: null,
            name: null,
            email: null,
        },
    ]);

    const [data, setData] = useState(getInitData(props));

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const isEditExistingData = !!interviewData;
    const { virtual, jobId, jobTitle } = data;

    const tiers = _get(sys, 'tiers', []);
    const subscribedTier = _get(currentEmployer, 'tier', 0);
    const companyName = _get(currentEmployer, 'companyName');
    const subscribedTierDetails = _find(tiers, { tier: subscribedTier });
    const activeJobMax = _get(subscribedTierDetails, 'activeJobMax');
    const jsEngageMax = _get(subscribedTierDetails, 'jsEngageMax');

    const members = _get(appContext, 'currentEmployer.members', []);
    const member = _find(members, o => o.userId === userId);
    const email = _get(member, 'email');
    const interviewersInvitedBy = email;

    //---------------------------------------------------------------------
    // useCountryStateCity hook
    //---------------------------------------------------------------------
    const {
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        // setCityLocation,
        // setCountryStateCity,
        countryProps,
        stateProps,
        cityProps,
        stateLabelProps,
    } = useCountryStateCity({
        defaultCountry,
        defaultState,
        defaultCity,
        defaultCityLocation,
        defaultCountryLov: _get(lovs, 'countryLov', []),
        setIsDirty,
        invalidRequiredFields,
        setInvalidRequiredFields,
        clearInvalidRequiredField,
        getFieldError,
    });

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const getPageIsValid = useCallback(() => {
        const { jobTitle, date, startTime, endTime, jobSeekerId, virtual, streetAddress1, postalCode } = data;
        const requiredFields = { jobTitle, date, startTime, endTime, jobSeekerId };

        if (virtual === 'physical') {
            _set(requiredFields, 'streetAddress1', streetAddress1);
            _set(requiredFields, 'country', country);
            _set(requiredFields, 'state', state);
            _set(requiredFields, 'city', city);
            _set(requiredFields, 'postalCode', postalCode);
        }

        return validateRequiredFields(requiredFields);
    }, [data, country, state, city, validateRequiredFields]);

    const handleClearAllButton = useCallback(() => {
        setData({
            jobSeekerId: null,
            jobSeekerName: null,
            jobSeekerProfilePicUrl: null,
            jobId: null,
            jobTitle: null,
            // pocNames: [], // deprecated
            date: new Date(),
            startTime: new Date().setMinutes('00'),
            endTime: new Date().setMinutes('30'),
            virtual: 'virtual',
            streetAddress1: '',
            streetAddress2: '',
            postalCode: '',
        });
        setCountry(null);
        setState(null);
        setCity(null);
        setShowClearAllModal(false);
    }, [setCountry, setState, setCity]);

    const cleanUpAndClose = useCallback(() => {
        if (unsubscribeOnSnapshotListRef.current) {
            unsubscribeRealtimeUpdates(unsubscribeOnSnapshotListRef.current);
            unsubscribeOnSnapshotListRef.current = [];
        }
        if (onClose) onClose();
        handleClearAllButton();
    }, [onClose, handleClearAllButton]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // load default job title suggestions if it has not been done so
    useEffect(() => {
        const loadInitialJobTitlesSuggestions = async () => {
            const options = await getEmployerActiveJobsSuggestions({ employerId, esToken, setEsToken });
            setActiveJobOptions(options);
        };
        if (_isNil(activeJobOptions)) {
            loadInitialJobTitlesSuggestions();
        }
    }, [employerId, activeJobOptions, setActiveJobOptions, esToken, setEsToken]);

    // useEffect(() => {
    //     if (open) {
    //         if (!isEditExistingData && !jobSeekerIdProp) {
    //             unsubscribeOnSnapshotListRef.current.push(getJobSeekers(setJobSeekers));
    //         } else if (isEditExistingData) {
    //             // the interview card does not have job seeker profile pic. Fetch it now.
    //             const jobSeekerId = _get(interviewData, 'jobSeekerId');
    //             getJobSeekerProfilePicUrl({
    //                 userId: jobSeekerId,
    //                 set: jobSeekerProfilePicUrl => {
    //                     setData(prevData => ({ ...prevData, jobSeekerProfilePicUrl }));
    //                 },
    //             });
    //         }
    //         unsubscribeOnSnapshotListRef.current.push(fetchEmployerActiveJobs(setActiveJobsLov, employerId));
    //     }

    //     return () => {
    //         unsubscribeRealtimeUpdates(unsubscribeOnSnapshotListRef.current);
    //     };
    // }, []);

    useEffect(() => {
        if (open) {
            if (isEditExistingData) {
                const interviewerList = _get(interviewData, 'interviewers', []);
                if (interviewerList?.length > 0) setInterviewers([...interviewerList]);
            }
        }
    }, [open, isEditExistingData, interviewData]);

    useEffect(() => {
        const saveNewJobInterview = async () => {
            const {
                jobId,
                date,
                jobTitle,
                pocNames,
                startTime,
                endTime,
                virtual,
                streetAddress1,
                streetAddress2,
                city,
                state,
                country,
                postalCode,
                // interviewers,
                jobSeekerId,
                jobSeekerName,
            } = data;

            const resp = await saveNewInterview({
                jobId,
                date,
                jobTitle,
                pocNames,
                startTime,
                endTime,
                virtual: virtual === 'virtual',
                streetAddress1,
                streetAddress2,
                city,
                cityLocation,
                state,
                country,
                postalCode,
                interviewers: interviewers.map(e => {
                    return { id: e.id, name: e.name, email: e.email };
                }),
                interviewersInvitedBy,
                activeJobMax,
                jsEngageMax,
                jobSeekerId,
                jobSeekerName,
                employerId,
                companyName,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            });

            if (resp?.success) {
                // clean up and close
                handleClearAllButton();
                cleanUpAndClose();
                logEvent(ANALYTICS_EVENT_INTERVIEW_CREATED);
            } else {
                // if failed with error, handle it.
                const errorCode = resp?.errorCode;
                if (errorCode) {
                    if (errorCode === 'engagement-limit-exceeded') {
                        setLimitModalOpen(true);
                    }
                }
            }
        };

        if (isOkToSave) {
            // limit not reached yet or js engaged in this job listing. Save record.
            if (isEditExistingData) {
                updateInterview({
                    data: {
                        ...data,
                        employerId,
                        interviewId,
                        country,
                        state,
                        city,
                        cityLocation,
                        virtual: virtual === 'virtual',
                        jobTitle,
                        jobId,
                        interviewers,
                    },
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                    postSaveActions: () => {
                        handleClearAllButton();
                        cleanUpAndClose();
                    },
                });
            } else {
                saveNewJobInterview();
            }
            setIsOkToSave(false);
        }
    }, [
        isOkToSave,
        employerId,
        currentEmployer,
        data,
        interviewId,
        isEditExistingData,
        virtual,
        jobId,
        jobTitle,
        country,
        state,
        city,
        cityLocation,
        companyName,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        handleClearAllButton,
        cleanUpAndClose,
        interviewers,
        authUser,
        activeJobMax,
        jsEngageMax,
        currentUser,
        interviewersInvitedBy,
    ]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setData({ ...data, [name]: value });
    };

    // const handleChangeAutoCompleteFields = (name, value) => {
    //     setData({ ...data, [name]: value });
    //     // setIsDirty(true);
    //     // clear error highlight
    //     // clearInvalidRequiredField(name);
    // };

    const handleChangeSend = (i, arr = []) => {
        const item = arr[arr.length - 1];
        const jobSeekerId = _get(item, 'uid');
        const jobSeekerName = getNameByUser(item);
        const jobSeekerProfilePicUrl = _get(item, 'profilePicUrl');
        setData(prev => ({
            ...prev,
            jobSeekerId,
            jobSeekerName,
            jobSeekerProfilePicUrl,
        }));
        setIsDirty(true);
    };

    const handleChangeJobTitle = (evt, job) => {
        const jobId = _get(job, 'uid', null);
        const jobTitle = _get(job, 'jobTitle', null);
        setData({ ...data, jobId, jobTitle });
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('jobTitle');
    };

    // deprecated

    // const handleChangePocNames = (evt, newVal) => {
    //     setData({ ...data, pocNames: newVal });
    //     setIsDirty(true);
    //     // clear error highlight
    //     clearInvalidRequiredField('pocNames');
    // };

    // deprecated
    // const handleKeyDownInterviewers = evt => {
    //     const { pocNames } = data;
    //     if (evt.keyCode === KEY_CODE_SEMICOLON) {
    //         const value = evt.target.value;
    //         if (value.length > 2) {
    //             setData({
    //                 ...data,
    //                 pocNames: [...pocNames, value],
    //             });
    //             setIsDirty(true);
    //             // clear error highlight
    //             clearInvalidRequiredField('pocNames');
    //         }
    //         evt.preventDefault();
    //     }
    // };

    // deprecated
    // const handleBlurInterviewers = evt => {
    //     const { pocNames } = data;
    //     const value = evt.target.value;
    //     if (value.length > 0) {
    //         setData({
    //             ...data,
    //             pocNames: [...pocNames, value],
    //         });
    //         setIsDirty(true);
    //         // clear error highlight
    //         clearInvalidRequiredField('pocNames');
    //     }
    // };

    const handleChangeDateTime = (name, newValue) => {
        setData({ ...data, [name]: newValue });
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField(name);
    };

    const handleChangeStartTime = newValue => {
        setData({ ...data, startTime: newValue, endTime: addMinutes(newValue, 30) });

        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('startTime');
        clearInvalidRequiredField('endTime');
    };

    const handleClickSaveButton = () => {
        if (getPageIsValid()) {
            setSendButtonModal(true);
        } else {
            setPageErrorDialogOpen(true);
        }
    };

    const handleClickConfirmSendYesButton = () => {
        setIsOkToSave(true);
    };

    const handleAnotherInterviewer = () => {
        setInterviewers([
            ...interviewers,
            {
                id: null,
                name: null,
                email: null,
            },
        ]);
    };

    const handleChangeInterviewerName = (e, index) => {
        // const { value } = e.target;
        // const newInterviewers = interviewers.map((interviewer, i) => {
        //     if (index === i) {
        //         return { name: value, email: interviewer?.email };
        //     } else return interviewer;
        // });
        // setInterviewers(newInterviewers);

        const { value } = e.target;
        setInterviewers(prev => {
            const updatedInterviewers = [...prev];
            _set(updatedInterviewers, `[${index}]`, { ...prev[index], name: value });
            return updatedInterviewers;
        });
    };

    const handleChangeInterviewerEmail = (e, index) => {
        // const { value } = e.target;
        // const newInterviewers = interviewers.map((interviewer, i) => {
        //     if (index === i) {
        //         if (validateEmail(value)) return { name: interviewer?.name, email: value };
        //         else return { name: interviewer?.name, email: value, emailError: true };
        //     } else return interviewer;
        // });
        // setInterviewers(newInterviewers);

        const { value } = e.target;
        const emailValid = validateEmail(value);
        setInterviewers(prev => {
            const updatedInterviewers = [...prev];
            _set(updatedInterviewers, `[${index}]`, { ...prev[index], email: value, emailError: !emailValid });
            return updatedInterviewers;
        });
    };

    const handleRemoveInterviewer = index => {
        // if (interviewers?.length > 1) {
        const newInterviewers = interviewers.filter((interviewer, i) => i !== index);
        setInterviewers(newInterviewers);
        // }
    };
    const onConfirmJsEngageLimitDialog = () => {
        setLimitModalOpen(false);
        history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION}`);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        employerId,
        esToken,
        setEsToken,
        isDirty,
        isEditExistingData,
        jobSeekers,
        handleClickSaveButton,
        handleClickConfirmSendYesButton,
        data,
        handleChangeFields,
        // handleChangeAutoCompleteFields,
        handleChangeSend,
        handleChangeJobTitle,
        // handleChangePocNames, // deprecated
        handleChangeDateTime,
        handleChangeStartTime,
        handleClearAllButton,
        setPopover,
        popover,
        setData,
        lovs,
        // statesLov,
        // citiesLov,
        myRef,
        showClearAllModal,
        setShowClearAllModal,
        showSendButtonModal,
        setSendButtonModal,
        cancelModal,
        setCancelModal,
        activeJobOptions,
        setActiveJobOptions,
        limitModalOpen,
        setLimitModalOpen,
        // handleKeyDownInterviewers, // deprecated
        // handleBlurInterviewers, // deprecated
        cleanUpAndClose,
        countryProps,
        stateProps,
        cityProps,
        stateLabelProps,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        onConfirmJsEngageLimitDialog,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <AfitdEditDialog {...defns.container}>
                <div {...defns.fieldContainer}>
                    <AfitdTypography {...defns.fieldLabel}>Send: *</AfitdTypography>
                    <AfitdAutocomplete {...defns.send} />
                </div>
                <div {...defns.fieldContainer}>
                    <AfitdTypography {...defns.fieldLabel}>Job Title: *</AfitdTypography>
                    <AfitdAutocomplete {...defns.jobTitle} />
                </div>
                <div {...defns.fieldContainer}>
                    <div {...defns.inline}>
                        <AfitdTypography {...defns.fieldLabel}>Interviewer Name(s):</AfitdTypography>
                        <InfoOutlinedIcon {...defns.infoIcon} />
                        <Popover {...defns.popover}>
                            <AfitdTypography>
                                Your colleagues who do not have an account with us can still participate! We will send them a meeting invite and link to the interview.
                            </AfitdTypography>
                        </Popover>
                    </div>
                    {interviewers?.map((interviewer, i) => (
                        <>
                            <Grid {...defns.interviewerContainer}>
                                <Grid {...defns.interviewerDetailContainer}>
                                    <Grid {...defns.interviewDetails}>
                                        <Grid {...defns.interviewerName}>
                                            <AfitdTextField value={interviewer?.name} placeholder="+ First and Last Name" onChange={e => handleChangeInterviewerName(e, i)} />
                                        </Grid>
                                        <Grid {...defns.interviewerEmail}>
                                            <AfitdTextField
                                                value={interviewer?.email}
                                                placeholder="+ Email Address"
                                                onChange={e => handleChangeInterviewerEmail(e, i)}
                                                error={interviewer?.emailError}
                                                helperText={interviewer?.emailError ? 'Please enter a valid email' : null}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid {...defns.interviewerActionButton}>
                                    {/* <AfitdButton className={classes.removeButton} >÷ */}
                                    <DeleteIcon onClick={() => handleRemoveInterviewer(i)} />
                                    {/* &nbsp;Remove */}
                                    {/* </AfitdButton> */}
                                </Grid>
                            </Grid>
                        </>
                    ))}
                    <div>
                        <AfitdButton color="secondary" onClick={handleAnotherInterviewer}>
                            <AddIcon />
                            &nbsp;{interviewers?.length > 0 ? 'Another Interviewer' : 'Interviewer'}
                        </AfitdButton>
                    </div>
                    {/* <AfitdAutocomplete {...defns.pocNames} /> */}
                </div>

                <div {...defns.fieldContainerGroup}>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Date: *</AfitdTypography>
                        <AfitdDatePicker {...defns.date} />
                    </div>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Time: *</AfitdTypography>
                        <div {...defns.fieldContainerGroup}>
                            <AfitdTimePicker {...defns.startTime} />
                            <AfitdTypography {...defns.timeText}>to</AfitdTypography>
                            <AfitdTimePicker {...defns.endTime} />
                        </div>
                    </div>
                </div>

                <div {...defns.fieldContainer}>
                    <FormControl>
                        <RadioGroup {...defns.radioGroup}>
                            <FormControlLabel {...defns.virtualLabel} />
                            <FormControlLabel {...defns.physicalLabel} />
                        </RadioGroup>
                    </FormControl>
                </div>

                <div {...defns.locationContainer}>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.streetAddress1Label}>Street 1: *</AfitdTypography>
                        <AfitdTextField {...defns.streetAddress1} />
                    </div>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Street 2:</AfitdTypography>
                        <AfitdTextField {...defns.streetAddress2} />
                    </div>

                    <div {...defns.fieldContainerGroup}>
                        <div {...defns.fieldContainer}>
                            <AfitdTypography {...defns.fieldLabel}>Country: *</AfitdTypography>
                            <AfitdCountryAutocomplete {...defns.country} />
                        </div>
                        <div {...defns.fieldContainer}>
                            <AfitdTypography {...defns.fieldLabel}>{defns.stateLabel.value} *</AfitdTypography>
                            <AfitdStateAutocomplete {...defns.state} />
                        </div>
                    </div>
                    <div {...defns.fieldContainerGroup} ref={myRef}>
                        <div {...defns.fieldContainer}>
                            <AfitdTypography {...defns.fieldLabel}>City: *</AfitdTypography>
                            <AfitdCityAutocomplete {...defns.city} />
                        </div>
                        <div {...defns.fieldContainer}>
                            <AfitdTypography {...defns.fieldLabel}>Zip / Postal Code: *</AfitdTypography>
                            <AfitdTextField {...defns.postalCode} />
                        </div>
                    </div>
                </div>
            </AfitdEditDialog>

            <AfitdConfirmDialog {...defns.confirmClearAllDialog} />
            <AfitdConfirmDialog {...defns.confirmSendDialog} />
            <AfitdConfirmDialog {...defns.confirmCancelDialog} />
            <AfitdConfirmDialog {...defns.limitDialog} />
            <AfitdMessageDialog {...defns.pageErrorDialog} />
        </div>
    );
};

export default InterviewDialog;
